import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressBar } from './components/ui/ProgressBar';
import { SlideTitle } from './components/SlideTitle';
import { SlideLifecycle } from './components/SlideLifecycle';
import { SlideProblem } from './components/SlideProblem';
import { SlideAIOpportunity } from './components/SlideAIOpportunity';
import { SlideCalculator } from './components/SlideCalculator';
import { SlideFlywheel } from './components/SlideFlywheel';
import { SlideCTALift } from './components/SlideCTALift';
import { SlideLoop } from './components/SlideLoop';
import { SlideGSKProof } from './components/SlideGSKProof';
import { SlideClose } from './components/SlideClose';
// Slide mapping (file → role):
// 0 SlideTitle         → S1 Regional Reality (hero + map)
// 1 SlideLifecycle     → S2 Portfolio Pressure Calculator
// 2 SlideProblem       → S3 Internal Time Calculator
// 3 SlideAIOpportunity → S4 Snakker Operating Layer
// 4 SlideCalculator    → S5 Engagement Yield Calculator
// 5 SlideFlywheel      → S6 Campaign Learning Curve
// 6 SlideCTALift       → S7 Engagement → CTA → Rx narrative
// 7 SlideLoop          → S8 The Snakker Flywheel
// 8 SlideGSKProof      → S9 GSK Africa Proof
// 9 SlideClose         → S10 Partnership Proposal
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const totalSlides = 10;
  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide, totalSlides]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') nextSlide();else if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlide = () => {
    switch (currentSlide) {
      case 0:
        return <SlideTitle onContinue={nextSlide} data-id="element-0" />;
      case 1:
        return <SlideLifecycle onContinue={nextSlide} data-id="element-1" />;
      case 2:
        return <SlideProblem onContinue={nextSlide} data-id="element-2" />;
      case 3:
        return <SlideAIOpportunity onContinue={nextSlide} data-id="element-3" />;
      case 4:
        return <SlideCalculator onContinue={nextSlide} data-id="element-4" />;
      case 5:
        return <SlideFlywheel onContinue={nextSlide} data-id="element-5" />;
      case 6:
        return <SlideCTALift onContinue={nextSlide} data-id="element-6" />;
      case 7:
        return <SlideLoop onContinue={nextSlide} data-id="element-7" />;
      case 8:
        return <SlideGSKProof onContinue={nextSlide} data-id="element-8" />;
      case 9:
        return <SlideClose data-id="element-9" />;
      default:
        return null;
    }
  };
  return <div className="w-full h-screen bg-navy-800 overflow-hidden relative font-sans text-white" data-id="element-10">
      <ProgressBar current={currentSlide} total={totalSlides} onBack={prevSlide} onSkip={nextSlide} data-id="element-11" />
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-12">
        <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        x: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.2
        }
      }} className="absolute inset-0 w-full h-full" data-id="element-13">
          {renderSlide()}
        </motion.div>
      </AnimatePresence>
    </div>;
}